import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Card, Row, Col, Spinner, Alert, Badge } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext'; // Importar para feedback
import { useAuthContext } from '../../context/AuthContext'; // Importar para obter o usuário logado

const initialConfigState = {
  email_host: '',
  email_port: '',
  email_user: '',
  email_pass: '',
  whatsapp_token: '',
  whatsapp_instance_id: '',
};

const ComunicadosConfigTab = () => {
  const { currentEntidade } = useEntidadeContext();
  const { showAlertModal } = useAlertModal();
  const { loggedInUser: currentUser } = useAuthContext();
  const [config, setConfig] = useState(initialConfigState);
  const [initialConfig, setInitialConfig] = useState(initialConfigState); // Para comparar alterações
  const [isLoading, setIsLoading] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false); // Novo estado para o teste
  const [isTestingWhatsApp, setIsTestingWhatsApp] = useState(false); // Novo estado para o teste de WhatsApp
  const [isEmailTestSuccessful, setIsEmailTestSuccessful] = useState(false); // Novo estado para o sucesso do teste

  // Verifica se as configurações estão completas para dar feedback visual
  const isEmailConfigComplete = useMemo(() => {
    // Retorna true apenas se todos os campos de e-mail estiverem preenchidos
    return !!(config.email_host && config.email_port && config.email_user && config.email_pass);
  }, [config]);

  const isWhatsAppConfigComplete = useMemo(() => {
    return !!(config.whatsapp_token && config.whatsapp_instance_id);
  }, [config]);

  // Verifica se a configuração de e-mail foi alterada desde que foi carregada
  const emailConfigChanged = useMemo(() => {
    if (!initialConfig) return false;
    return (
      config.email_host !== initialConfig.email_host ||
      String(config.email_port) !== String(initialConfig.email_port) || // Compara como string para evitar problemas de tipo
      config.email_user !== initialConfig.email_user ||
      config.email_pass !== initialConfig.email_pass
    );
  }, [config, initialConfig]);

  // Verifica se qualquer configuração foi alterada
  const isChanged = useMemo(() => {
    return JSON.stringify(config) !== JSON.stringify(initialConfig);
  }, [config, initialConfig]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!currentEntidade) return;
      setIsLoading(true);
      setIsEmailTestSuccessful(false); // Reseta o status do teste ao trocar de entidade
      try {
        const response = await axios.get(`/api/entidades/${currentEntidade.id}/comunicacao-config`);
        // Se a API retornar uma config, usa ela. Senão, reseta para o estado inicial.
        const fetchedConfig = response.data.config || initialConfigState;
        setConfig(fetchedConfig);
        setInitialConfig(fetchedConfig); // Armazena o estado inicial
      } catch (error) {
        console.error("Erro ao buscar configurações de comunicação:", error);
        setConfig(initialConfigState);
        setInitialConfig(initialConfigState); // Reseta o estado inicial em caso de erro
        showAlertModal('Erro', 'Não foi possível carregar as configurações de comunicação.', 'warning');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [currentEntidade, showAlertModal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prevConfig => ({ ...prevConfig, [name]: value }));

    // Se qualquer configuração de e-mail for alterada, o teste bem-sucedido não é mais válido.
    if (['email_host', 'email_port', 'email_user', 'email_pass'].includes(name)) {
      setIsEmailTestSuccessful(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentEntidade) return;
    setIsLoading(true);
    try {
      const response = await axios.post(`/api/entidades/${currentEntidade.id}/comunicacao-config`, config);
      showAlertModal('Sucesso', 'Configurações salvas com sucesso!', 'success');

      const savedConfig = response.data.config || initialConfigState;
      setConfig(savedConfig);
      setInitialConfig(savedConfig); // Atualiza o estado inicial para refletir o que foi salvo
      setIsEmailTestSuccessful(false); // Reseta o status do teste após salvar
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      showAlertModal('Erro', `Falha ao salvar as configurações: ${error.response?.data?.message || 'Erro desconhecido.'}`, 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestEmailConnection = async () => {
    if (!isEmailConfigComplete) {
      showAlertModal('Atenção', 'Preencha todos os campos de configuração de e-mail antes de testar.', 'warning');
      return;
    }
    if (!currentUser?.email) {
      showAlertModal('Erro', 'Não foi possível identificar o e-mail do usuário logado para enviar o teste.', 'danger');
      return;
    }

    setIsTestingConnection(true);
    setIsEmailTestSuccessful(false); // Reseta antes de cada teste
    try {
      // A API espera os dados da configuração para testar
      await axios.post('/api/test-email-connection', {
        host: config.email_host,
        port: config.email_port,
        username: config.email_user,
        password: config.email_pass,
        recipient_email: currentUser.email, // Envia o e-mail do usuário logado como destinatário do teste
        entidade_id: currentEntidade.id // Adiciona o ID da entidade para buscar o logo
      });
      showAlertModal('Sucesso', `Conexão bem-sucedida! Um e-mail de teste foi enviado para ${currentUser.email}.`, 'success');
      setIsEmailTestSuccessful(true); // Marca o teste como bem-sucedido
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Falha na conexão. Verifique as credenciais e configurações de SMTP.';
      showAlertModal('Erro de Conexão', errorMessage, 'danger');
      setIsEmailTestSuccessful(false);
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleTestWhatsAppConnection = async () => {
    if (!isWhatsAppConfigComplete) {
      showAlertModal('Atenção', 'Preencha o Token da API e o ID da Instância antes de testar.', 'warning');
      return;
    }
    if (!currentUser?.dddcelular || !currentUser?.celular) {
      showAlertModal('Erro', 'Não foi possível identificar o número de telefone do usuário logado para enviar o teste.', 'danger');
      return;
    }

    setIsTestingWhatsApp(true);
    try {
      const recipientPhone = `55${currentUser.dddcelular}${currentUser.celular}`;
      // A API espera os dados da configuração para testar
      await axios.post('/api/test-whatsapp-connection', {
        token: config.whatsapp_token,
        instance_id: config.whatsapp_instance_id,
        recipient_phone: recipientPhone,
      });
      showAlertModal('Sucesso', `Conexão bem-sucedida! Uma mensagem de teste foi enviada para ${recipientPhone}.`, 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Falha na conexão. Verifique o Token e o ID da Instância.';
      showAlertModal('Erro de Conexão', errorMessage, 'danger');
    } finally {
      setIsTestingWhatsApp(false);
    }
  };


  return (
    <Card>
      <Card.Header>
        <Card.Title>Configurações de Envio (E-mail e WhatsApp)</Card.Title>
      </Card.Header>
      <Card.Body>
        <Card className="mb-4 bg-light border-0 shadow-sm">
          <Card.Body>
            <Card.Title as="h6">Para que servem estas configurações?</Card.Title>
            <Card.Text className="small text-muted">
              As configurações de E-mail (SMTP) e WhatsApp são essenciais para que o sistema possa enviar comunicados, lembretes de agendamento e promoções para os seus pacientes.
              Preencha os dados corretamente e realize os testes de conexão para garantir o funcionamento.
            </Card.Text>
          </Card.Body>
        </Card>
        {isLoading ? (
          <div className="text-center"><Spinner animation="border" /></div>
        ) : (
          <Form onSubmit={handleSave}>
            <h5>
              Configurações de E-mail (SMTP)
              <Badge bg={isEmailConfigComplete ? 'success' : 'warning'} className="ms-2 align-middle">
                {isEmailConfigComplete ? 'Completo' : 'Incompleto'}
              </Badge>
            </h5>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Servidor (Host)</Form.Label><Form.Control type="text" name="email_host" value={config.email_host} onChange={handleChange} placeholder="smtp.seuprovedor.com" /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Porta</Form.Label><Form.Control type="text" name="email_port" value={config.email_port} onChange={handleChange} placeholder="587" /></Form.Group></Col>
            </Row>
            <Row>
              <Col md={6}><Form.Group className="mb-3"><Form.Label>Usuário</Form.Label><Form.Control type="text" name="email_user" value={config.email_user} onChange={handleChange} placeholder="seu_email@provedor.com" /></Form.Group></Col>
              <Col md={6}><Form.Group className="mb-3">
                <Form.Label>Senha</Form.Label>
                <Form.Control type="password" name="email_pass" value={config.email_pass} onChange={handleChange} placeholder="********" />
                <Form.Text muted>
                  Usa Gmail? <a href="https://support.google.com/mail/answer/185833?hl=pt-BR" target="_blank" rel="noopener noreferrer">Veja como criar uma senha de app</a>.
                </Form.Text>
                </Form.Group></Col>
            </Row>
            <hr />
            <h5>
              Configurações de WhatsApp
              <Badge bg={'secondary'} className="ms-2 align-middle">
                Em breve
              </Badge>
            </h5>
            <Form.Group className="mb-3"><Form.Label>Token da API</Form.Label><Form.Control type="text" name="whatsapp_token" value={config.whatsapp_token} onChange={handleChange} disabled /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>ID da Instância</Form.Label><Form.Control type="text" name="whatsapp_instance_id" value={config.whatsapp_instance_id} onChange={handleChange} disabled /></Form.Group>
            <div className="d-flex justify-content-end flex-wrap">
              <Button variant="secondary" onClick={handleTestEmailConnection} disabled={isTestingConnection || !isEmailConfigComplete} className="me-2 mb-2">
                {isTestingConnection ? <><Spinner as="span" animation="border" size="sm" /> Testando...</> : 'Testar Conexão E-mail'}
              </Button>
              <Button variant="primary" onClick={handleTestWhatsAppConnection} disabled={true} className="me-2 mb-2">
                Testar Conexão WhatsApp
              </Button>
              <Button
                variant="success"
                type="submit"
                disabled={isLoading || !isChanged || (emailConfigChanged && !isEmailTestSuccessful)}
                className="me-2 mb-2"
                title={emailConfigChanged && !isEmailTestSuccessful ? 'Você precisa testar a conexão de e-mail após alterar os dados.' : !isChanged ? 'Nenhuma alteração para salvar.' : ''}
              >
                {isLoading ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </div>
          </Form>
        )}
      </Card.Body>
    </Card>
  );
};

export default ComunicadosConfigTab;